import { Control, FieldError, FieldValues, useController } from "react-hook-form";
import ReactSelect, {
  ClearIndicatorProps,
  components,
  DropdownIndicatorProps,
  GroupBase,
  Props
} from "react-select";
import { mdiChevronDown, mdiClose } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";

import { getClassByVariant } from "helpers/getClassByVariant";
import { IOption } from "models/Form";
import styles from "./style.module.scss";
import { useColorScheme } from "hooks/useColorScheme";

interface IProps<
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
> extends Props<Option, IsMulti, Group> {
  defaultOption?: IOption;
  changeCallback?: (value: IOption) => void;
  control?: Control<FieldValues>;
  label: string | React.ReactNode;
  className?: string;
  error?: FieldError;
  disabled?: boolean;
  color?: "primary" | "secondary";
  innerClassName?: string;
}

const DropdownIndicator = ({ ...props }: DropdownIndicatorProps) => (
  <components.DropdownIndicator {...props}>
    <Icon path={mdiChevronDown} className="text-text" />
  </components.DropdownIndicator>
);

function ClearIndicator<Option, IsMulti extends boolean, Group extends GroupBase<Option>>({
  ...props
}: ClearIndicatorProps<Option, IsMulti, Group>) {
  return (
    <components.ClearIndicator {...props}>
      <Icon path={mdiClose} />
    </components.ClearIndicator>
  );
}

export default function AutoComplete<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>({
  name,
  label,
  defaultOption,
  control,
  changeCallback,
  options,
  className,
  error,
  disabled = false,
  color = "primary",
  innerClassName,
  ...props
}: Readonly<IProps<Option, IsMulti, Group>>) {
  const {
    field: { value, onChange, onBlur }
  } = useController({
    name: name ?? "",
    control: control,
    defaultValue: defaultOption
  });

  const { currentIsDark } = useColorScheme();

  const handleSelect = (option: IOption) => {
    onChange(option);
    if (changeCallback) changeCallback(option);
  };

  return (
    <div className={clsx(styles.smartAutocomplete, className)}>
      {label && (
        <label className={clsx(styles.smartAutocompleteLabel, getClassByVariant(color)?.label)}>
          {label}
        </label>
      )}
      <ReactSelect
        className={innerClassName}
        closeMenuOnSelect={false}
        onChange={(item) => handleSelect(item as IOption)}
        onBlur={onBlur}
        value={value}
        components={{ DropdownIndicator, ClearIndicator }}
        options={options}
        isDisabled={disabled}
        styles={{
          container: (provided) => ({
            ...provided,
            ...(!!error && {
              borderBottom: "1px",
              borderStyle: "solid",
              borderColor: "rgb(var(--error))",
              borderRadius: "10px"
            })
          }),
          control: (provided) => ({
            ...provided,
            backgroundColor: "rgb(var(--input))",
            border: "0px",
            minHeight: "50px",
            borderRadius: "10px",
            paddingLeft: "12px",
            boxShadow: "0 0"
          }),
          multiValue: (baseStyles) => ({
            ...baseStyles,
            backgroundColor: currentIsDark ? "#608AB21F" : "#d6d6d699"
          }),
          multiValueLabel: (baseStyles) => ({
            ...baseStyles,
            color: currentIsDark ? "white" : "#1f2025"
          }),
          multiValueRemove: (baseStyles) => ({
            ...baseStyles,
            color: currentIsDark ? "white" : "#1f2025"
          }),
          menu: (baseStyles) => ({
            ...baseStyles,
            maxHeight: "200px",
            backgroundColor: currentIsDark ? "#2b313a" : "white",
            color: currentIsDark ? "white" : "#101010"
          }),
          menuList: (baseStyles) => ({
            ...baseStyles,
            maxHeight: "200px",
            borderRadius: "8px"
          }),
          placeholder: (baseStyles) => ({
            ...baseStyles,
            color: "#9ca3af"
          }),
          option: (baseStyles) => ({
            ...baseStyles,
            backgroundColor: currentIsDark ? "#2b313a" : "white"
          }),
          input: (baseStyles) => ({
            ...baseStyles,
            color: currentIsDark ? "white" : "#101010"
          })
        }}
        {...props}
      />

      {error && <span className="text-sm leading-5 text-error">{error?.message}</span>}
    </div>
  );
}
